import { keepPreviousData, useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { BurpParseResponse, useBurpParse } from '@/hooks/api/useBurpParse';
import { useModifyAccount } from '@/hooks/useAccounts';
import { useAxios } from '@/hooks/useAxios';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { getSeedTypeConfig } from '@/sections/seeds/config/seedTypeConfigs';
import { SeedType } from '@/sections/seeds/types';
import { useAuth } from '@/state/auth';
import {
  AddSeedFileObj,
  GraphQuery,
  Preseed,
  QueryFilter,
  ResourceType,
  Seed,
  SeedStatus,
  SeedTypeOption,
} from '@/types';
import { useMergeStatus } from '@/utils/api';
import { Regex } from '@/utils/regex.util';

export function useUpdateSeed({
  query,
  invalidateSeed = true,
}: {
  query?: string;
  invalidateSeed?: boolean;
} = {}) {
  const axios = useAxios();
  const { invalidate: invalidateGenericSearch } = useGenericSearch(
    {
      query,
      label: ResourceType.Seed,
    },
    { enabled: false }
  );

  return useMutation({
    mutationFn: async ({
      identifier,
      status,
      comment,
      remove,
      showSnackbar = true,
      type = 'asset',
    }: {
      key?: string;
      identifier?: string;
      status?: SeedStatus;
      comment?: string;
      remove?: number;
      showSnackbar?: boolean;
      type: 'asset' | 'webapplication';
    }) => {
      let promise: Promise<unknown>;

      if (type === 'webapplication') {
        promise = axios.put('/seed', {
          type: 'webapplication',
          model: {
            primary_url: identifier,
            status,
            comment,
            remove,
          },
        });
      } else {
        promise = axios.put('/seed', {
          type: 'asset',
          identifier,
          status,
          comment,
          remove,
        });
      }

      if (showSnackbar) {
        toast.promise(promise, {
          loading: 'Updating seeds',
          success: 'Seeds updated successfully',
          error: 'Failed to update seeds',
        });
      }

      await promise;

      if (invalidateSeed) {
        // Use predicate-based invalidation to catch ALL asset queries
        // This ensures both table and drawer views refetch
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'asset';
          },
          refetchType: 'active',
        });

        // Use predicate-based invalidation to catch ALL seed queries
        // This ensures both table and drawer views refetch
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'seed';
          },
          refetchType: 'active',
        });

        invalidateGenericSearch();
      }
    },
  });
}

export function useHardDeleteSeed() {
  const axios = useAxios();
  const { invalidate: invalidateGenericSearch } = useGenericSearch(
    {
      label: ResourceType.Seed,
    },
    { enabled: false }
  );

  return useMutation({
    mutationFn: async ({ key, name }: { key: string; name?: string }) => {
      const promise = axios.delete('/seed/purge', {
        data: {
          key,
        },
      });

      toast.promise(promise, {
        loading: `Permanently deleting ${name ?? 'seed'}`,
        success: `Permanently deleted ${name ?? 'seed'}`,
        error: `Failed to permanently delete ${name ?? 'seed'}`,
      });

      await promise;

      // Use predicate-based invalidation to catch ALL asset queries
      // This ensures both table and drawer views refetch
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
        refetchType: 'active',
      });

      // Use predicate-based invalidation to catch ALL seed queries
      // This ensures both table and drawer views refetch
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'seed';
        },
        refetchType: 'active',
      });

      invalidateGenericSearch();
    },
  });
}

export const isDomainFn = (query: string) => query.includes('#domain');

export type Sort = {
  orderBy: keyof Seed | keyof Preseed;
  descending: boolean;
};

export type SeedFilterKeys = Exclude<keyof Seed, 'key'>;

export interface SeedFilter {
  type: SeedType;
  search?: string;
  advancedFilters?: Record<string, string[]>;
  sort?: Sort;
  limit?: number;
  negatedFilterKeys?: string[];
}

interface BulkAddSeedsParams {
  seeds?: string[]; // Optional array of seed strings
  type: SeedTypeOption;
  files?: AddSeedFileObj[];
  burpType?: SeedTypeOption;
}

/**
 * Filters, search, sorting do not work together
 */
export function useAddSeeds() {
  const axios = useAxios();
  const { mutateAsync: addSeeds } = useModifyAccount('link', undefined, true);
  const { me, isPraetorianUser } = useAuth();
  const { mutateAsync: validateBurpParse } = useBurpParse();

  // Auto-approval settings
  const autoApproveEnabled = isPraetorianUser;
  const myDomain = me?.split('@')[1];

  const calculateSeedStatus = (seedValue?: string): SeedStatus => {
    let updatedStatus = SeedStatus.Pending;
    if (
      autoApproveEnabled ||
      (myDomain && seedValue && seedValue.includes(myDomain))
    ) {
      updatedStatus = SeedStatus.Accept;
    }
    return updatedStatus;
  };

  return useMutation({
    mutationFn: async ({
      seeds,
      type,
      files,
      burpType,
    }: BulkAddSeedsParams) => {
      const errors: string[] = [];

      // Maps each raw input URL to the burp-resolved primary_url the asset is
      // actually stored under (Burp strips the path to the origin). The wizard's
      // credential-attach step keys the broker ResourceKey off this so it matches
      // the stored WebApplication node instead of the raw input line.
      const resolvedUrls: Record<string, string> = {};

      switch (type) {
        case SeedTypeOption.WEB_APPLICATION: {
          if (files && files.length > 0) {
            const promise = Promise.allSettled(
              files.map(async file => {
                const status = calculateSeedStatus();
                // await uploadFile({ name, content: fileObj.content });
                try {
                  await addSeeds({
                    username: 'seed-import',
                    value: file.name,
                    config: { status },
                  });
                } catch (error) {
                  errors.push(
                    `Failed to queue web application import job for ${file.name}`
                  );
                  throw error;
                }
              })
            );

            toast.promise(customToastPromise(promise, errors), {
              loading: `Queueing ${files.length} web application import jobs`,
              success: `Successfully queued ${files.length} web application import jobs`,
              error: 'Failed to queue web application import jobs',
            });
            await promise;
          } else if (seeds && seeds.length > 0) {
            const promise = Promise.allSettled(
              seeds.map(async identifier => {
                const status = calculateSeedStatus(identifier);

                try {
                  await axios.put('/seed', {
                    type: 'webapplication',
                    model: { primary_url: identifier, status },
                  });
                } catch (error) {
                  errors.push(`Failed to add web application: ${identifier}`);
                  throw error;
                }
              })
            );

            toast.promise(customToastPromise(promise, errors), {
              loading: `Adding ${seeds.length} web applications`,
              success: `Successfully added ${seeds.length} web applications`,
              error: 'Failed to add web applications',
            });

            await promise;
          } else {
            throw new Error(
              'Please enter seeds in the text area or upload a file'
            );
          }

          break;
        }

        case SeedTypeOption.DOMAIN_CIDR: {
          // ASSET (Domain/CIDR) logic
          if (files && files.length > 0) {
            const promise = Promise.allSettled(
              files.map(async file => {
                const status = calculateSeedStatus();
                // await uploadFile({ name, content: fileObj.content });
                try {
                  await addSeeds({
                    username: 'seed-import',
                    value: file.name,
                    config: { status },
                  });
                } catch (error) {
                  errors.push(
                    `Failed to queue seed import job for ${file.name}`
                  );
                  throw error;
                }
              })
            );

            toast.promise(customToastPromise(promise, errors), {
              loading: `Queueing ${files.length} seed import jobs`,
              success: `Successfully queued ${files.length} seed import jobs`,
              error: 'Failed to queue seed import jobs',
            });
            await promise;
          } else if (seeds && seeds.length > 0) {
            // Seeds array is present - use seeds directly
            const promise = Promise.allSettled(
              seeds.map(async identifier => {
                const status = calculateSeedStatus(identifier);

                try {
                  await axios.put('/seed', {
                    type: 'asset',
                    identifier,
                    status,
                  });
                } catch (error) {
                  errors.push(`Failed to add seed: ${identifier}`);
                  throw error;
                }
              })
            );

            toast.promise(customToastPromise(promise, errors), {
              loading: `Adding ${seeds.length} seeds`,
              success: `Successfully added ${seeds.length} seeds`,
              error: 'Failed to add seeds',
            });

            await promise;
          } else {
            throw new Error(
              'Please enter seeds in the text area or upload a file'
            );
          }

          break;
        }

        case SeedTypeOption.WEB_SERVICE: {
          if (files && files.length > 0) {
            const promise = Promise.allSettled(
              files.map(async file => {
                const status = calculateSeedStatus(file.primary_url);
                const resolvedBurpType =
                  burpType ?? file.burp_type ?? SeedTypeOption.WEB_SERVICE;

                try {
                  return axios.put('/seed', {
                    type: 'webapplication',
                    model: {
                      primary_url: file.primary_url,
                      status: status,
                      details_path: file.details_path,
                      burp_type: resolvedBurpType,
                    },
                  });
                } catch (error) {
                  errors.push(`Failed to add web service: ${file.name}`);
                  throw error;
                }
              })
            );

            toast.promise(customToastPromise(promise, errors), {
              loading: `Adding ${files.length} web services`,
              success: `Successfully added ${files.length} web services`,
              error: 'Failed to add web services',
            });

            await promise;
          } else if (seeds && seeds.length > 0) {
            const promise = Promise.allSettled(
              seeds.map(async url => {
                let result: BurpParseResponse | undefined;

                try {
                  result = await validateBurpParse({ url });
                } catch (error) {
                  errors.push(`Failed to validate web service: ${url}`);
                  throw error;
                }

                if (result) {
                  resolvedUrls[url] = result.primary_url;
                  const status = calculateSeedStatus(result.primary_url);
                  const resolvedBurpType =
                    burpType ?? SeedTypeOption.WEB_SERVICE;

                  try {
                    await axios.put('/seed', {
                      type: 'webapplication',
                      model: {
                        primary_url: result.primary_url,
                        status,
                        details_path: result.filepath,
                        burp_type: resolvedBurpType,
                      },
                    });
                  } catch (error) {
                    errors.push(`Failed to add web service: ${url}`);
                    throw error;
                  }
                }
              })
            );

            toast.promise(customToastPromise(promise, errors), {
              loading: `Adding ${seeds.length} web services`,
              success: `Successfully added ${seeds.length} web services`,
              error: 'Failed to add web services',
            });

            await promise;
          } else {
            throw new Error('Please provide seeds or webServiceFiles');
          }

          break;
        }

        default:
          throw new Error(`Unsupported seed type: ${type}`);
      }

      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
        refetchType: 'active',
      });
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'seed';
        },
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey.genericSearch(''),
      });

      if (errors.length > 0) {
        throw new Error(errors.join('\n'));
      }

      return { success: true, method: 'bulk-add', resolvedUrls };
    },
  });
}

/**
 * Polymorphic seed data hook - handles all seed types including preseed
 *
 * Supports 4 seed types:
 * - domain: Domain and TLD seeds
 * - ip: IPv4, IPv6, and CIDR seeds
 * - webapplication: Web application seeds
 * - preseed: Attribute preseeds (enriched with count data)
 *
 * **Phase 3 Enhancement**: Now handles preseed type with count enrichment
 *
 * @param type - The seed type to fetch
 * @param search - Search query string
 * @param sort - Sort configuration
 * @param advancedFilters - Advanced filter object
 * @param enabled - Whether to enable the query
 * @param limit - Query limit
 *
 * @example
 * ```typescript
 * // Fetch domain seeds
 * const { data, status } = useSeeds({ type: 'domain', search: 'example.com' });
 *
 * // Fetch preseed with count enrichment
 * const { data, status } = useSeeds({ type: 'preseed' });
 * ```
 */
export function useSeeds({
  search,
  type: tableType,
  sort,
  advancedFilters,
  enabled = true,
  limit,
}: SeedFilter & { enabled?: boolean }) {
  const config = getSeedTypeConfig(tableType);

  // Determine query parameters based on type
  const queryParams = useMemo(() => {
    if (tableType === 'preseed') {
      // Preseed uses different query structure
      return {
        resource: 'preseed' as const,
        labels: ['Preseed'] as ['Preseed'],
        classFilter: null,
      };
    } else if (tableType === 'webapplication') {
      return {
        resource: 'seed' as const,
        labels: ['Seed', 'WebApplication'] as ['Seed', 'WebApplication'],
        classFilter: null,
      };
    } else {
      // Domain and IP use class filter
      return {
        resource: 'seed' as const,
        labels: ['Seed'] as ['Seed'],
        classFilter: {
          field: 'class',
          operator: '=' as const,
          value: config.classValues,
        },
      };
    }
  }, [tableType, config.classValues]);

  // Track previous type to prevent keepPreviousData from keeping data across seed type changes.
  // When the type changes (tab switch), we don't want stale data from the previous type to briefly
  // appear as placeholder data while the new type's query loads.
  const [prevType, setPrevType] = useState(tableType);
  const isSameType = prevType === tableType;
  useEffect(() => {
    setPrevType(tableType);
  }, [tableType]);

  // Build filter array (same for all types)
  // Note: Direct advancedFilters reference is safe - React does deep equality comparison
  // useMemo will only recompute when the actual values change, not object references
  const { nodeFilters, isFiltersApplied, searchParam } = useMemo(() => {
    const updatedFilters: GraphQuery['node']['filters'] = [];

    // Fulltext search for all seed types:
    // - preseed: preseedSearchIndex (covers key, title, type, value)
    // - domain/ip: seedSearchIndex (covers username, key, name, dns, identifier)
    // - webapplication: seedSearchIndex (covers username, key, name, dns, identifier)
    const searchParam = search && search.length >= 2 ? search : undefined;

    if (advancedFilters) {
      Object.entries(advancedFilters).forEach(([key, value]) => {
        if (!value || value.length === 0) return;

        // For 'class' field, use '=' operator with single array
        if (key === 'class') {
          updatedFilters.push({
            field: key,
            operator: '=',
            value: value, // ["domain", "tld"]
          });
        } else if (key === 'profile') {
          // Profile filter: empty string means IS NULL (omitempty field)
          const nullValues = value.filter(v => v === '');
          const nonNullValues = value.filter(v => v !== '');
          if (nullValues.length > 0) {
            updatedFilters.push({
              field: key,
              operator: 'IS NULL' as const,
              value: '',
            });
          }
          if (nonNullValues.length > 0) {
            updatedFilters.push({
              field: key,
              operator: 'IN' as const,
              value: [nonNullValues],
            });
          }
        } else if (key === 'origins') {
          // Origins is a []string array on the node — each value needs its
          // own "value IN node.origins" check (reverseOperands), OR'd together.
          // Manual (@praetorian.com) uses ENDS WITH on source field.
          const hasManual = value.includes('@praetorian.com');
          const nonManualOrigins = value.filter(v => v !== '@praetorian.com');

          const orFilters: QueryFilter[] = nonManualOrigins.map(origin => ({
            field: 'origins',
            operator: 'IN' as const,
            value: origin,
            reverseOperands: true,
          }));

          if (hasManual) {
            orFilters.push({
              field: 'source',
              operator: 'ENDS WITH',
              value: '@praetorian.com',
            });
          }

          if (orFilters.length === 1) {
            updatedFilters.push(orFilters[0]);
          } else if (orFilters.length > 1) {
            updatedFilters.push({
              field: '',
              operator: 'OR',
              value: orFilters,
            });
          }
        } else {
          // For other fields, use 'IN' operator with double brackets
          updatedFilters.push({
            field: key,
            operator: 'IN' as const,
            value: [value], // [["P"]] - wrap in additional array for IN operator
          });
        }
      });
    }

    return {
      nodeFilters: updatedFilters,
      isFiltersApplied: updatedFilters.length > 0 || !!searchParam,
      searchParam,
    };
  }, [advancedFilters, search]);

  // All query (for empty state detection). Its ONLY consumer is `isAllEmpty`
  // below (`allQuery.data.length === 0`), so it never needs more than one row —
  // default to `limit: 1` when no caller-supplied limit is present. This also
  // keeps the probe cheaper server-side AND makes it self-identifying: a
  // top-level `limit` with no `orderBy` is the stable discriminator
  // `useRefreshSeedData.invalidateSeeds` relies on to skip refetching this probe
  // on every WHOIS-refresh tick. Keep `limit` unconditionally present here — the
  // predicate goes inert (allQuery over-fetches again) if this key is dropped.
  const allQuery = useMy(
    {
      resource: queryParams.resource,
      query: {
        limit: limit ?? 1,
        node: {
          labels: queryParams.labels,
          filters: queryParams.classFilter ? [queryParams.classFilter] : [],
        },
      },
    },
    { enabled, refetchOnWindowFocus: false }
  );

  // Filtered query
  const query = useMy(
    {
      resource: queryParams.resource,
      query: {
        node: {
          labels: queryParams.labels,
          filters: queryParams.classFilter
            ? [queryParams.classFilter, ...nodeFilters]
            : nodeFilters,
          // Fulltext search for all seed types (seedSearchIndex / preseedSearchIndex)
          ...(searchParam ? { search: searchParam } : {}),
        },
        ...(sort ? { orderBy: sort.orderBy, descending: sort.descending } : {}),
      },
    },
    {
      enabled,
      // Keep previous data visible during search to prevent blanking on every keystroke
      // Search is excluded from filterKey, so table doesn't remount
      // Filter/sort changes still remount via filterKey for scroll reset
      // Only keep previous data within the same seed type (prevents cross-tab data contamination)
      placeholderData: isSameType ? keepPreviousData : undefined,
      // Opt into keyset (cursor) pagination (fixes the Domains-tab `visited`
      // DESC dup/drop: near-total ties break offset SKIP/LIMIT at page bounds).
      // Keyset is LIVE NOW for ALL seeds sorts, `status` included: the backend's
      // NormalizeCursorSort bridges the legacy single-field `orderBy` into the
      // structured `orderByFields` the engine gates on, the default `visited`
      // sort (nullable primary) rides the two-phase NULL machinery, and `status`
      // now sorts by the seed/asset lifecycle CASE ordinal (Active=1 [A + runtime
      // AL/AP/AH via STARTS WITH 'A'], Pending=2, Frozen=3, Frozen-Rejected=4,
      // Unavailable=5, Deleted=6, ELSE 9) — a non-null expression, so keyset can
      // engage. The OFFSET fallback path emits the SAME CASE, so a Status sort's
      // order is identical whether or not keyset engages (no more dup/drop and no
      // more raw-alphabetical status order for seeds — a deliberate, product-facing
      // change; risk/vuln surfaces are unchanged). ENG-4175.
      cursorPagination: true,
    }
  );

  // Count query (only for preseed)
  const countQuery = useMy(
    {
      resource: 'statistic',
      query: '#preseed#count#now',
    },
    { enabled: enabled && tableType === 'preseed' }
  );

  // If preseed, enrich with count data
  const enrichedData = useMemo(() => {
    if (tableType !== 'preseed') {
      return query.data;
    }

    // Preseed enrichment logic (from usePreseed)
    return query.data?.map(preseed => {
      const [, type, title, value] = Regex.PRESEED_KEY.exec(preseed.key) || [];
      const statisticKey = `#statistic#preseed#count#now##preseed#${type}#${title}#${value}`;
      return {
        ...preseed,
        count:
          countQuery.data?.find(
            statistic =>
              statistic.key.length > 0 && statisticKey.startsWith(statistic.key)
          )?.data?.count || 0,
      };
    });
  }, [tableType, query.data, countQuery.data]);

  // Merge status from all queries
  const status = useMergeStatus(
    allQuery.status,
    query.status,
    ...(tableType === 'preseed' ? [countQuery.status] : [])
  );

  // Build activeQuery unconditionally so bulk selection ("Select all matching") and export work regardless of whether filters are applied
  const activeQuery = useMemo(() => {
    return {
      node: {
        labels: queryParams.labels,
        filters: queryParams.classFilter
          ? [queryParams.classFilter, ...nodeFilters]
          : nodeFilters,
        ...(searchParam ? { search: searchParam } : {}),
      },
      ...(sort ? { orderBy: sort.orderBy, descending: sort.descending } : {}),
    };
  }, [queryParams, nodeFilters, searchParam, sort]);

  return {
    data: (enrichedData ?? []) as (Seed | Preseed)[],
    error: allQuery.error || query.error || countQuery.error,
    status,
    fetchNextPage: query.fetchNextPage,
    isFetching: query.isFetching || false,
    isAllFetching: allQuery.isFetching || false,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    isAllEmpty: status !== 'pending' && allQuery.data.length === 0,
    isFilteredEmpty: status !== 'pending' && (enrichedData?.length ?? 0) === 0,
    isFiltersApplied,
    activeQuery: activeQuery as GraphQuery | undefined,
    refetch: () => {
      allQuery.refetch();
      query.refetch();
      if (tableType === 'preseed') {
        countQuery.refetch();
      }
    },
  };
}

const customToastPromise = (promise: Promise<unknown>, errors: string[]) => {
  return promise.then(() => {
    if (errors.length === 0) return;
    throw new Error(errors.join('\n'));
  });
};
